import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

export interface MeterBarProps {
  /** 0–100. Anything outside is clamped rather than drawn off the end. */
  value: number;
  /** Shown to the right of the bar, already formatted: "94", "81%". */
  readout?: string;
  /** What the bar is, to the left of it. */
  label?: string;
  /**
   * `brand` — the ordinary case.
   * `good` / `warning` / `bad` — a figure that carries a judgment, e.g. an
   * agent at 96% of capacity.
   */
  tone?: 'brand' | 'good' | 'warning' | 'bad';
  /** `sm` sits inside a table row; `md` stands on its own. */
  size?: 'sm' | 'md';
  /** Stops the bar filling the row, for a column that should stay narrow. */
  width?: number;
  /**
   * What the bar shows, for screen readers, when there is no visible `label`:
   * "Maintenance (Mx): 60.7% of operator IROPs".
   */
  ariaLabel?: string;
  /**
   * The figure a screen reader hears, 0–100, when the bar is drawn to another
   * scale — a share drawn against the largest row, say. Defaults to `value`.
   */
  ariaValue?: number;
}

/**
 * A proportion, drawn.
 *
 * For the places a number alone reads flat — a score in a queue, an agent's
 * remaining capacity — where the eye should be able to rank rows without
 * reading them. Deliberately plain: no gradient, no animation, no axis. The
 * number beside it stays the source of truth; the bar is there to be scanned.
 */
export function MeterBar({
  value,
  readout,
  label,
  tone = 'brand',
  size = 'sm',
  width,
  ariaLabel,
  ariaValue,
}: MeterBarProps) {
  const clamp = (n: number) => Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));
  const pct = clamp(value);
  // A meter needs a name. Without one — no aria label, visible label or
  // readout — the bar is decoration, and is hidden from screen readers.
  const name = ariaLabel ?? label ?? readout;
  const meter = name
    ? {
        role: 'meter',
        'aria-valuemin': 0,
        'aria-valuemax': 100,
        'aria-valuenow': Math.round(clamp(ariaValue ?? value) * 10) / 10,
        'aria-label': name,
      }
    : { 'aria-hidden': true };
  const height = size === 'sm' ? 6 : 8;

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0, width: width ?? '100%' }}>
      {label ? (
        <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary, whiteSpace: 'nowrap' })}>
          {label}
        </Typography>
      ) : null}
      <Box
        {...meter}
        sx={(t) => ({
          flex: 1,
          minWidth: 40,
          height,
          borderRadius: `${t.ds.radius.xs}px`,
          bgcolor: t.tokens.surface.sunken,
          overflow: 'hidden',
        })}
      >
        <Box
          sx={(t) => ({
            width: `${pct}%`,
            height: '100%',
            borderRadius: `${t.ds.radius.xs}px`,
            bgcolor:
              tone === 'good' ? t.tokens.semantic.success
                : tone === 'warning' ? t.tokens.semantic.warning
                  : tone === 'bad' ? t.tokens.semantic.danger
                    : t.tokens.brand.primary,
            transition: `width ${t.ds.motion.duration.normal}ms ${t.ds.motion.easing.standard}`,
          })}
        />
      </Box>
      {readout ? (
        <Typography
          variant="caption"
          sx={(t) => ({
            color: t.tokens.text.primary,
            fontVariantNumeric: 'tabular-nums',
            minWidth: 26,
            textAlign: 'right',
          })}
        >
          {readout}
        </Typography>
      ) : null}
    </Stack>
  );
}

export default MeterBar;
